import { useCallback, useState, useMemo, type MutableRefObject } from "react";
import { useFetchOrganizationLogos } from "@core/features/organization";
import { LogosUploadContainer } from "@platform/features/programOrgAdmin";
import { useStepFormSync } from "@platform/features/onboarding";
import type { StepValidationState } from "@platform/features/onboarding";
import type { FileType } from "@core/types/file.types";
import { Loader } from "@themes/pollinator/ui/Loader";
import { OrgLogoStep } from "./OrgLogoStep";

export type Props = {
  programId: number;
  organizationId: number;
  onComplete: () => void;
  onSkip?: () => void;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
};

/**
 * Org logo upload sub-step container.
 * Uses the existing LogosUploadContainer to handle file upload to S3.
 * Enables the Continue button once at least one logo exists.
 */
export function OrgLogoStepContainer({
  organizationId,
  onComplete,
  onStepStateChange,
  submitRef,
}: Props) {
  const [uploadState, setUploadState] = useState<{
    hasFiles: boolean;
    isAltTextValid: boolean;
  }>({ hasFiles: false, isAltTextValid: true });

  const { data: logos, isLoading } = useFetchOrganizationLogos({
    organizationId,
    enabled: !!organizationId,
  });

  const hasExistingLogo = (logos?.length ?? 0) > 0;

  // Map organization logos to FileType for the uploader
  // Only show the most recent logo for onboarding (maxFiles=1)
  const initialFiles: FileType[] = useMemo(() => {
    if (!logos || logos.length === 0) return [];
    // Take only the first (most recent) logo
    const logo = logos[0];
    return [{
      id: logo.id,
      name: logo.name,
      file: logo.file,
      size: String(logo.size),
      progress: 100,
      error: false,
      checked: false,
      mimeType: logo.file_type === "logo" ? "image/png" : logo.file_type,
      // Existing logos are considered to have valid alt text (they were saved before)
      altText: logo.name,
    }];
  }, [logos]);

  const handleSubmit = useCallback(() => {
    onComplete();
  }, [onComplete]);

  // Can proceed if:
  // - Uploaded files in this session: require alt text to be valid
  // - No uploaded files: allow if there's an existing logo
  const canProceed = uploadState.hasFiles
    ? uploadState.isAltTextValid
    : hasExistingLogo;

  useStepFormSync({
    shouldSync: true,
    onStepStateChange,
    submitRef,
    formState: { isValid: true, isDirty: true, isSubmitting: false },
    onSubmit: handleSubmit,
    canProceed,
  });

  if (isLoading) {
    return <Loader isFullPage />;
  }

  return (
    <OrgLogoStep>
      <LogosUploadContainer
        organizationId={organizationId}
        maxFiles={1}
        onUploadStateChange={setUploadState}
        initialFiles={initialFiles}
        showRemoveButton
      />
    </OrgLogoStep>
  );
}

export default OrgLogoStepContainer;
